import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { DEFAULT_URL_STATE, type LogsUrlState, type LogsMode } from "./types";

/**
 * Bridges logs viewer state to URL search params so every filter/mode/pod
 * selection is a shareable deep link and survives browser refresh.
 *
 * Why URL state instead of useState: copy/paste a URL to share a live tail
 * scoped to a pod+level+search term; React Router v7 already exposes
 * `useSearchParams` so we don't pull in another state library.
 */
export function useLogsUrlState() {
  const [params, setParams] = useSearchParams();

  const state: LogsUrlState = useMemo(
    () => ({
      mode: (params.get("mode") as LogsMode) || DEFAULT_URL_STATE.mode,
      pod: params.get("pod") ?? DEFAULT_URL_STATE.pod,
      container: params.get("container") ?? DEFAULT_URL_STATE.container,
      levels: params.get("levels")?.split(",").filter(Boolean) ?? DEFAULT_URL_STATE.levels,
      q: params.get("q") ?? DEFAULT_URL_STATE.q,
      range: params.get("range") ?? DEFAULT_URL_STATE.range,
      cid: params.get("cid") ?? DEFAULT_URL_STATE.cid,
    }),
    [params]
  );

  const update = useCallback(
    (patch: Partial<LogsUrlState>) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged: LogsUrlState = { ...state, ...patch };

          const setOrDelete = (key: keyof LogsUrlState, value: string) => {
            if (value && value !== DEFAULT_URL_STATE[key]) {
              next.set(key, value);
            } else {
              next.delete(key);
            }
          };

          setOrDelete("mode", merged.mode);
          setOrDelete("pod", merged.pod);
          setOrDelete("container", merged.container);
          setOrDelete("q", merged.q);
          setOrDelete("range", merged.range);
          setOrDelete("cid", merged.cid);

          if (merged.levels.length > 0) {
            next.set("levels", merged.levels.join(","));
          } else {
            next.delete("levels");
          }

          return next;
        },
        { replace: true }
      );
    },
    [state, setParams]
  );

  const reset = useCallback(() => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const key of ["mode", "pod", "container", "levels", "q", "range", "cid"] as const) {
          next.delete(key);
        }
        return next;
      },
      { replace: true }
    );
  }, [setParams]);

  return { state, update, reset };
}
